'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useReactiveRef } from './useReactiveRef';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import { getOrCreateCallId } from '@/lib/calls/getOrCreateCallId';
import {
  OrchestratorTreeState,
  hydrateOrchestratorTreeState,
  selectTreeSyncInputs,
} from '@/store/slices/orchestratorTreeSlice';
import { RootState } from '@/store/store';
import type { ConversationTree } from '@/components/reasoning/types';

/**
 * Orchestrator Tree Real-Time Sync Hook
 *
 * Provides real-time Supabase sync for orchestrator/reasoning tree state.
 * - Debounced writes (2000ms) - tree changes are less frequent than typing
 * - Hydrates from Supabase on mount
 * - Persists both tree structure and UI state (expanded nodes)
 *
 * This enables:
 * - Recovery of tree state on page refresh
 * - Viewing historical trees in incident reports
 * - Cross-device continuity during calls
 */

const SYNC_DEBOUNCE_MS = 2000; // Tree changes are less frequent, use longer debounce

interface UseOrchestratorTreeSyncOptions {
  /** Call ID (room name) for scoping */
  callId: string;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
  /** Custom debounce time in ms */
  debounceMs?: number;
}

interface OrchestratorTreeSyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: Error | null;
  dbCallId: string | null;
}

// Database schema for orchestrator tree
interface DbOrchestratorTree {
  call_id: string;
  tree: ConversationTree | null;
  expanded_node_ids: string[];
  selected_node_id: string | null;
  is_detail_panel_open: boolean;
  is_strategy_panel_collapsed: boolean;
  local_version: number;
  full_state: Partial<OrchestratorTreeState> | null;
}

/**
 * Compute hash for tree state to detect changes
 */
function computeTreeHash(state: OrchestratorTreeState): string {
  return JSON.stringify({
    treeId: state.tree?.id,
    treeVersion: state.tree?.metadata?.version,
    currentNodeId: state.tree?.currentNodeId,
    expandedCount: state.expandedNodeIds?.length,
    // Include a sample of expanded IDs for more accurate change detection
    expandedSample: state.expandedNodeIds?.slice(0, 5),
    selectedNodeId: state.selectedNodeId,
    isDetailPanelOpen: state.isDetailPanelOpen,
    isStrategyPanelCollapsed: state.isStrategyPanelCollapsed,
  });
}

/**
 * Hook to sync orchestrator tree to Supabase in real-time
 */
export function useOrchestratorTreeSync(
  options: UseOrchestratorTreeSyncOptions
): OrchestratorTreeSyncState & { reloadFromSupabase: () => Promise<void> } {
  const { callId, enabled = true, debounceMs = SYNC_DEBOUNCE_MS } = options;

  const dispatch = useDispatch();
  const { user, isLoading: authLoading } = useAuth();
  const supabase = createClient();

  const store = useStore<RootState>();
  // Only the fields the sync effect schedules on (shallowEqual): a viewport
  // interaction, auto-view flip or transition stamp must not re-render the
  // component that calls this hook (Conference). The rest of the persisted
  // state (autoView, transition stamps, localVersion) is read from the store at
  // sync time.
  const orchestratorTree = useSelector(selectTreeSyncInputs, shallowEqual);

  // Refs for tracking state without triggering re-renders
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedHashRef = useRef<string | null>(null);
  const dbCallIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const hasLoadedRef = useRef(false);
  const stateRef = useReactiveRef<OrchestratorTreeSyncState>({
    isLoading: true,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
    dbCallId: null,
  });

  /**
   * Get or create the DB call ID (UUID) for this call
   */
  const getOrCreateDbCallId = useCallback(async (): Promise<string | null> => {
    if (dbCallIdRef.current) {
      return dbCallIdRef.current;
    }

    try {
      const dbCallId = await getOrCreateCallId(supabase, callId, {
        userId: user?.id ?? null,
      });
      if (dbCallId) {
        dbCallIdRef.current = dbCallId;
      }
      return dbCallId;
    } catch (err: unknown) {
      logger.error('useOrchestratorTreeSync', 'Failed to get/create call ID', {
        error: err,
        callId,
      });
      return null;
    }
  }, [callId, supabase, user?.id]);

  /**
   * Sync tree state to Supabase
   */
  const syncToSupabase = useCallback(
    async (treeState: OrchestratorTreeState) => {
      if (!enabled || !callId) return;

      // Skip if no tree exists yet
      if (!treeState.tree) {
        logger.debug(
          'useOrchestratorTreeSync',
          'Skipping sync - no tree exists'
        );
        return;
      }

      // Compute hash to avoid redundant syncs
      const hash = computeTreeHash(treeState);

      if (hash === lastSyncedHashRef.current) {
        logger.debug('useOrchestratorTreeSync', 'Skipping sync - no changes');
        return;
      }

      stateRef.current = { ...stateRef.current, isSyncing: true };

      try {
        const dbCallId = await getOrCreateDbCallId();
        if (!dbCallId) {
          throw new Error('Failed to get/create call ID');
        }

        // Build the DB payload
        const dbPayload: DbOrchestratorTree = {
          call_id: dbCallId,
          tree: treeState.tree,
          expanded_node_ids: treeState.expandedNodeIds,
          selected_node_id: treeState.selectedNodeId,
          is_detail_panel_open: treeState.isDetailPanelOpen,
          is_strategy_panel_collapsed: treeState.isStrategyPanelCollapsed,
          local_version: treeState.localVersion,
          // Store full state for complete recovery
          full_state: {
            tree: treeState.tree,
            expandedNodeIds: treeState.expandedNodeIds,
            selectedNodeId: treeState.selectedNodeId,
            isDetailPanelOpen: treeState.isDetailPanelOpen,
            isStrategyPanelCollapsed: treeState.isStrategyPanelCollapsed,
            localVersion: treeState.localVersion,
            autoViewEnabled: treeState.autoViewEnabled,
            previousCurrentNodeId: treeState.previousCurrentNodeId,
            lastTransitionTime: treeState.lastTransitionTime,
          },
        };

        // Upsert tree state
        const { error } = await supabase.from('orchestrator_trees').upsert(
          dbPayload,
          {
            onConflict: 'call_id',
          }
        );

        if (error) {
          throw error;
        }

        lastSyncedHashRef.current = hash;
        stateRef.current = {
          ...stateRef.current,
          isSyncing: false,
          lastSyncTime: Date.now(),
          error: null,
          dbCallId,
        };

        logger.debug('useOrchestratorTreeSync', 'Synced tree to Supabase', {
          callId,
          treeId: treeState.tree?.id,
          version: treeState.tree?.metadata?.version,
          expandedCount: treeState.expandedNodeIds?.length,
        });
      } catch (err: unknown) {
        logger.error('useOrchestratorTreeSync', 'Failed to sync to Supabase', {
          error: err,
        });
        stateRef.current = {
          ...stateRef.current,
          isSyncing: false,
          error: err instanceof Error ? err : new Error(String(err)),
        };
      }
    },
    [enabled, callId, getOrCreateDbCallId, supabase]
  );

  /**
   * Load tree state from Supabase.
   *
   * Default behavior (no opts): one-shot on mount — uses hasLoadedRef guard.
   * Reconnect callers pass { force: true } to bypass the guard and re-pull
   * the latest tree from `conversation_tree_snapshots` (per Codex Risk #3,
   * 2026-05-28: tree was advancing on backend during the disconnect window
   * but the client UI stayed at the pre-disconnect snapshot until the stuck-
   * detection watchdog fired ~45s later).
   */
  const loadFromSupabase = useCallback(async (opts?: { force?: boolean }) => {
    if (!enabled || !callId) {
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }
    if (!opts?.force && hasLoadedRef.current) {
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }

    hasLoadedRef.current = true;

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (!callData?.id) {
        stateRef.current = { ...stateRef.current, isLoading: false };
        return;
      }

      dbCallIdRef.current = callData.id;

      // Read both possible sources of truth:
      //   1. orchestrator_trees — frontend-written "latest snapshot" row,
      //      keyed on calls.id (UUID). Includes UI state (expandedNodeIds,
      //      selectedNodeId, panel toggles).
      //   2. conversation_tree_snapshots — agent-backend-written historical
      //      snapshots, keyed on calls.call_id (room name TEXT). The newest
      //      row is the live tree as last seen by the backend.
      //
      // On a frontend crash + rejoin the orchestrator_trees row goes stale
      // (its sole writer was the crashed FE), while conversation_tree_snapshots
      // keeps growing because the backend keeps writing it. We must prefer
      // whichever source has the newer tree.
      const [{ data: treeData, error: treeError }, { data: snapshotData, error: snapshotError }] =
        await Promise.all([
          supabase
            .from('orchestrator_trees')
            .select('*')
            .eq('call_id', callData.id)
            .maybeSingle(),
          supabase
            .from('conversation_tree_snapshots')
            .select('tree_json, version, timestamp, created_at')
            .eq('call_id', callId)
            .order('timestamp', { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);

      if (treeError && treeError.code !== 'PGRST116') {
        throw treeError;
      }
      if (snapshotError && snapshotError.code !== 'PGRST116') {
        // Don't throw on snapshot read errors — fall back to orchestrator_trees only.
        logger.warn(
          'useOrchestratorTreeSync',
          'Failed to read conversation_tree_snapshots; falling back to orchestrator_trees only',
          { error: snapshotError, callId }
        );
      }

      if (mountedRef.current && (treeData || snapshotData)) {
        // Build state from orchestrator_trees full_state (or columns).
        const orchestratorState: Partial<OrchestratorTreeState> | null = treeData
          ? treeData.full_state
            ? (treeData.full_state as Partial<OrchestratorTreeState>)
            : {
                tree: treeData.tree as ConversationTree | null,
                expandedNodeIds: (treeData.expanded_node_ids as string[]) || [],
                selectedNodeId: treeData.selected_node_id,
                isDetailPanelOpen: treeData.is_detail_panel_open,
                isStrategyPanelCollapsed: treeData.is_strategy_panel_collapsed,
                localVersion: treeData.local_version,
              }
          : null;

        const snapshotTree = snapshotData?.tree_json as ConversationTree | null;

        // Decide which tree to hydrate from. Prefer the snapshot tree if it
        // has a newer metadata.version than the orchestrator_trees tree, OR
        // if orchestrator_trees has no tree at all. Otherwise keep the
        // orchestrator_trees tree (which carries UI state like expansions).
        const orchestratorVersion =
          orchestratorState?.tree?.metadata?.version ?? -1;
        const snapshotVersion =
          (snapshotData?.version as number | undefined) ??
          snapshotTree?.metadata?.version ??
          -1;

        let loadedState: Partial<OrchestratorTreeState>;
        let chosenSource: 'orchestrator_trees' | 'conversation_tree_snapshots' | 'merged';

        if (snapshotTree && snapshotVersion > orchestratorVersion) {
          // Snapshot is fresher — use its tree, but preserve orchestrator UI state.
          loadedState = {
            ...(orchestratorState || {}),
            tree: snapshotTree,
            // Bump localVersion to match so subsequent diffs use the right basis.
            localVersion: orchestratorState?.localVersion ?? 0,
          };
          chosenSource =
            orchestratorState && orchestratorState.tree
              ? 'merged'
              : 'conversation_tree_snapshots';
        } else if (orchestratorState) {
          loadedState = orchestratorState;
          chosenSource = 'orchestrator_trees';
        } else if (snapshotTree) {
          loadedState = { tree: snapshotTree, expandedNodeIds: [] };
          chosenSource = 'conversation_tree_snapshots';
        } else {
          loadedState = {};
          chosenSource = 'orchestrator_trees';
        }

        // Dispatch to Redux to restore state on rejoin
        dispatch(hydrateOrchestratorTreeState(loadedState));

        // Update hash based on LOADED state to prevent immediate re-sync
        lastSyncedHashRef.current = computeTreeHash(
          loadedState as OrchestratorTreeState
        );

        logger.info(
          'useOrchestratorTreeSync',
          'Loaded tree from Supabase and hydrated Redux',
          {
            source: chosenSource,
            treeId: loadedState.tree?.id,
            version: loadedState.tree?.metadata?.version,
            orchestratorVersion,
            snapshotVersion,
            expandedCount: loadedState.expandedNodeIds?.length,
          }
        );
      }

      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        dbCallId: callData.id,
      };
    } catch (err: unknown) {
      logger.error('useOrchestratorTreeSync', 'Failed to load from Supabase', {
        error: err,
      });
      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, supabase, dispatch]);

  // Load from Supabase on mount
  useEffect(() => {
    if (!enabled || authLoading) return;

    loadFromSupabase();
  }, [enabled, authLoading, loadFromSupabase]);

  // Debounced sync when tree state changes
  useEffect(() => {
    if (!enabled || !callId) return;

    // CRITICAL (#305): Do NOT write back to orchestrator_trees until the
    // initial DB load has completed. Otherwise a rejoined session — whose
    // Redux orchestratorTree is still empty / stub — will fire the debounced
    // upsert and overwrite the live orchestrator_trees row before
    // loadFromSupabase resolves, blowing away the very state we are about
    // to hydrate from. hasLoadedRef.current is set inside loadFromSupabase
    // before the dispatch so by the time we get here for a real change,
    // hydration is already in flight or done.
    if (!hasLoadedRef.current || stateRef.current.isLoading) {
      return;
    }

    // Cancel pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule new sync (only if tree exists)
    if (orchestratorTree.tree) {
      syncTimeoutRef.current = setTimeout(() => {
        syncToSupabase(store.getState().orchestratorTree);
      }, debounceMs);
    }
  }, [
    enabled,
    callId,
    // Watch key state changes
    orchestratorTree.tree?.id,
    orchestratorTree.tree?.metadata?.version,
    orchestratorTree.tree?.currentNodeId,
    orchestratorTree.expandedNodeIds,
    orchestratorTree.selectedNodeId,
    orchestratorTree.isDetailPanelOpen,
    orchestratorTree.isStrategyPanelCollapsed,
    debounceMs,
    syncToSupabase,
    store,
  ]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Reload the tree from Supabase, bypassing the hasLoadedRef one-shot guard.
   * Call on RoomEvent.Reconnected to backfill tree advances that happened
   * while the client was offline. Per Codex Risk #3 (2026-05-28).
   */
  const reloadFromSupabase = useCallback(async () => {
    if (!enabled || !callId) return;
    logger.info('useOrchestratorTreeSync', 'Force reloading orchestrator tree from Supabase (reconnect)', { callId });
    await loadFromSupabase({ force: true });
  }, [enabled, callId, loadFromSupabase]);

  // Attach reloadFromSupabase to the state object returned to consumers, in
  // addition to the existing OrchestratorTreeSyncState fields. Consumers can
  // call this on Reconnected without depending on a separate hook.
  return {
    ...stateRef.current,
    reloadFromSupabase,
  };
}

/**
 * Hook to hydrate orchestrator tree from storage
 */
export function useOrchestratorTreeHydration(callId: string): {
  hydrate: () => Promise<Partial<OrchestratorTreeState> | null>;
  isHydrated: boolean;
} {
  const supabase = createClient();
  const hydratedRef = useRef(false);

  const hydrate =
    useCallback(async (): Promise<Partial<OrchestratorTreeState> | null> => {
      if (hydratedRef.current || !callId) return null;

      try {
        let result: Partial<OrchestratorTreeState> | null = null;

        const { data: callData } = await supabase
          .from('calls')
          .select('id')
          .eq('call_id', callId)
          .maybeSingle();

        if (callData?.id) {
          const { data: treeData } = await supabase
            .from('orchestrator_trees')
            .select('*')
            .eq('call_id', callData.id)
            .maybeSingle();

          if (treeData?.full_state) {
            result = treeData.full_state as Partial<OrchestratorTreeState>;
            logger.info(
              'useOrchestratorTreeHydration',
              'Hydrated from Supabase',
              {
                treeId: result?.tree?.id,
              }
            );
          } else if (treeData?.tree) {
            // Fall back to individual columns if no full_state
            result = {
              tree: treeData.tree as ConversationTree,
              expandedNodeIds: (treeData.expanded_node_ids as string[]) || [],
              selectedNodeId: treeData.selected_node_id,
              isDetailPanelOpen: treeData.is_detail_panel_open,
              isStrategyPanelCollapsed: treeData.is_strategy_panel_collapsed,
              localVersion: treeData.local_version,
            };
            logger.info(
              'useOrchestratorTreeHydration',
              'Hydrated from Supabase columns',
              {
                treeId: result?.tree?.id,
              }
            );
          }
        }

        hydratedRef.current = true;
        return result;
      } catch (err: unknown) {
        logger.error('useOrchestratorTreeHydration', 'Hydration failed', {
          error: err,
        });
        return null;
      }
    }, [callId, supabase]);

  return {
    hydrate,
    isHydrated: hydratedRef.current,
  };
}

export default useOrchestratorTreeSync;
